import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import facturaVentaService, { type FacturaVenta } from '../services/facturaVentaService';

import companiaService, { type Compania } from '../services/companiaService';

import sucVentaService, { type SucVenta } from '../services/sucVentaService';

import {
  debeImprimirTicketEscPos,
  motivoNoTicketEscPos,
  nombreImpresoraTicketSucVenta,
  NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA,
} from '../utils/posImpresionConfig';
import type { ModoImpresionFacturaVenta } from '../utils/posImpresionConfig';

import {
  cargarMapaSucVentaPorClave,
  cargarMapaUbicacionNroSuc,
  claveCacheSucVenta,
  resolverContextoSucVentaImpresion,
  resolverModoImpresionFactura,
  type ContextoImpresionFacturaInput,
  type SucVentaMapEntry,
} from '../utils/sucVentaImpresionResolver';

import { fetchFacturaVentaPdf } from '../utils/facturaVentaPdf';

import { buildFacturaVentaTicketPrintLines } from '../utils/escpos/facturaVentaTicketPrintLines';

import {
  PrintHelperUnavailableError,
  printTicketLines,
} from '../services/ticketPrinterService';

export interface PosPdfViewerState {
  url: string;
  nroFormulario: number;
  fileName: string | null;
}

export type FacturaVentaImpresionInput = Pick<FacturaVenta, 'nroFormulario'> &
  Partial<Omit<FacturaVenta, 'nroFormulario'>>;

export interface UsePosTicketPrinterOptions {
  nroTipoFormulario?: string;
  ubicacionEtiqueta?: string;
  nroUbicacionOrigen?: number;
}

export function usePosTicketPrinter(
  nroSucDefault: number,
  options?: UsePosTicketPrinterOptions,
) {
  const { enqueueSnackbar } = useSnackbar();
  const [printing, setPrinting] = useState(false);
  const [pdfFactura, setPdfFactura] = useState<PosPdfViewerState | null>(null);
  const [ubicacionPorNro, setUbicacionPorNro] = useState<Record<number, number>>({});
  const [sucVentaPorClave, setSucVentaPorClave] = useState<Record<string, SucVentaMapEntry>>({});
  const [mapasListos, setMapasListos] = useState(false);
  const [sucVentaCache, setSucVentaCache] = useState<{
    clave: string;
    data: SucVenta;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([cargarMapaUbicacionNroSuc(), cargarMapaSucVentaPorClave()])
      .then(([ubicaciones, sucVentas]) => {
        if (cancelled) return;
        setUbicacionPorNro(ubicaciones);
        setSucVentaPorClave(sucVentas);
        setMapasListos(true);
      })
      .catch(() => {
        if (!cancelled) {
          setUbicacionPorNro({});
          setSucVentaPorClave({});
          setMapasListos(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const inputPosDefault = useMemo<ContextoImpresionFacturaInput>(
    () => ({
      nroSuc: nroSucDefault,
      nroTipoFormulario: options?.nroTipoFormulario,
      nroUbicacionOrigen: options?.nroUbicacionOrigen,
      ubicacion: options?.ubicacionEtiqueta,
    }),
    [
      nroSucDefault,
      options?.nroTipoFormulario,
      options?.nroUbicacionOrigen,
      options?.ubicacionEtiqueta,
    ],
  );

  const modoImpresionPos = useMemo<ModoImpresionFacturaVenta | null>(() => {
    if (!mapasListos) return null;
    return resolverModoImpresionFactura(inputPosDefault, sucVentaPorClave, ubicacionPorNro);
  }, [inputPosDefault, mapasListos, sucVentaPorClave, ubicacionPorNro]);

  const contextoPosTicket = useMemo(
    () =>
      resolverContextoSucVentaImpresion(
        inputPosDefault,
        ubicacionPorNro,
        sucVentaPorClave,
        'ticket',
        options?.nroTipoFormulario ?? NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA,
      ),
    [inputPosDefault, ubicacionPorNro, sucVentaPorClave, options?.nroTipoFormulario],
  );

  const closePdfFactura = useCallback(() => {
    setPdfFactura((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const resolverContextoDesdeFactura = useCallback(
    (factura?: ContextoImpresionFacturaInput | null, modo: 'ticket' | 'impresion' | 'cualquiera' = 'cualquiera') =>
      resolverContextoSucVentaImpresion(
        {
          nroSuc: factura?.nroSuc ?? nroSucDefault,
          nroTipoFormulario: factura?.nroTipoFormulario ?? options?.nroTipoFormulario,
          nroUbicacionOrigen: factura?.nroUbicacionOrigen ?? options?.nroUbicacionOrigen,
          ubicacion: factura?.ubicacion ?? options?.ubicacionEtiqueta,
        },
        ubicacionPorNro,
        sucVentaPorClave,
        modo,
        options?.nroTipoFormulario ?? NRO_TIPO_FORMULARIO_FACTURA_VENTA_SUCVENTA,
      ),
    [
      nroSucDefault,
      options?.nroTipoFormulario,
      options?.nroUbicacionOrigen,
      options?.ubicacionEtiqueta,
      ubicacionPorNro,
      sucVentaPorClave,
    ],
  );

  const cargarSucVentaImpresion = useCallback(
    async (
      contexto: ContextoImpresionFacturaInput,
      modo: 'ticket' | 'impresion' | 'cualquiera' = 'ticket',
    ): Promise<SucVenta | null> => {
      const ctx = resolverContextoDesdeFactura(contexto, modo);
      if (!ctx.nroSuc || ctx.nroSuc <= 0) return null;

      const clave = claveCacheSucVenta(ctx);
      if (sucVentaCache?.clave === clave) return sucVentaCache.data;

      try {
        const sv = await sucVentaService.getSucVenta(ctx.nroSuc, ctx.nroTipoFormulario);
        setSucVentaCache({ clave, data: sv });
        return sv;
      } catch {
        return null;
      }
    },
    [resolverContextoDesdeFactura, sucVentaCache],
  );

  useEffect(() => {
    if (modoImpresionPos === 'ticketera' && contextoPosTicket.nroSuc > 0) {
      void cargarSucVentaImpresion(
        {
          nroSuc: contextoPosTicket.nroSuc,
          nroTipoFormulario: contextoPosTicket.nroTipoFormulario,
        },
        'ticket',
      );
    }
  }, [modoImpresionPos, contextoPosTicket, cargarSucVentaImpresion]);

  const abrirPdfFactura = useCallback(
    async (nroFormulario: number) => {
      try {
        const { url, fileName } = await fetchFacturaVentaPdf(nroFormulario);
        setPdfFactura((prev) => {
          if (prev?.url) window.URL.revokeObjectURL(prev.url);
          return { url, nroFormulario, fileName };
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Error al generar el PDF de la factura';
        enqueueSnackbar(msg, { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  const imprimirFactura = useCallback(
    async (
      facturaResumen: FacturaVentaImpresionInput | null | undefined,
      printOptions?: { forzarPdf?: boolean; forzarTicket?: boolean },
    ) => {
      const nroFormulario = Number(facturaResumen?.nroFormulario) || 0;
      if (!nroFormulario) return;

      const ctx = resolverContextoDesdeFactura(facturaResumen ?? undefined, 'ticket');

      setPrinting(true);
      try {
        if (printOptions?.forzarPdf) {
          await abrirPdfFactura(nroFormulario);
          return;
        }

        const sucVenta = await cargarSucVentaImpresion(facturaResumen ?? {}, 'ticket');
        const ticketConfigurado = debeImprimirTicketEscPos(sucVenta);

        if (printOptions?.forzarTicket && !ticketConfigurado) {
          const motivo = motivoNoTicketEscPos(sucVenta, ctx.nroSuc, ctx.nroTipoFormulario);
          if (motivo) {
            enqueueSnackbar(motivo, { variant: 'warning', autoHideDuration: 8000 });
          }
          return;
        }

        if (!ticketConfigurado) {
          return;
        }

        const factura = await facturaVentaService.getFacturaVenta(nroFormulario);

        let compania: Compania | null = null;
        const idCompania = String(factura.idCompania ?? '').trim();
        if (idCompania) {
          try {
            compania = await companiaService.getCompania(idCompania);
          } catch {
            compania = null;
          }
        }
        if (!compania) {
          const list = await companiaService.getCompanias({ pageSize: 1, pageNumber: 1 });
          compania = list[0] ?? null;
        }
        if (!compania) {
          enqueueSnackbar('No se pudo cargar datos de la compañía; se abre PDF.', { variant: 'warning' });
          await abrirPdfFactura(nroFormulario);
          return;
        }

        const ticketLines = buildFacturaVentaTicketPrintLines(factura, {
          compania,
          incluirQr: Boolean(factura.caeNro),
        });
        const printerName = nombreImpresoraTicketSucVenta(sucVenta, factura.letra);
        await printTicketLines(ticketLines, printerName);
        enqueueSnackbar('Ticket enviado a la impresora', { variant: 'success' });
      } catch (e: unknown) {
        if (e instanceof PrintHelperUnavailableError) {
          enqueueSnackbar(
            'Iniciá eOne Print Helper en este PC (tools/eone-print-helper). Se abre PDF.',
            { variant: 'warning', autoHideDuration: 10000 },
          );
        } else {
          const msg = e instanceof Error ? e.message : 'Error al imprimir ticket';
          enqueueSnackbar(`${msg}. Se intenta PDF…`, { variant: 'warning', autoHideDuration: 8000 });
        }
        try {
          await abrirPdfFactura(nroFormulario);
        } catch {
          enqueueSnackbar('Tampoco se pudo generar el PDF', { variant: 'error' });
        }
      } finally {
        setPrinting(false);
      }
    },
    [abrirPdfFactura, cargarSucVentaImpresion, enqueueSnackbar, resolverContextoDesdeFactura],
  );

  return {
    printing,
    pdfFactura,
    closePdfFactura,
    imprimirFactura,
    abrirPdfFactura,
    modoImpresionPos,
  };
}
